import { useCallback, useMemo, useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Link, useLocation } from "react-router-dom"
import { ShoppingCart, Mail, Menu, X, MessageCircleMore } from "lucide-react"

import Inner from "./Inner"
import LanguageSelect from "./LanguageSelect"
import CurrencySelect from "./CurrencySelect"
import { routes } from "../routes"
import Breadcrumbs from "./Breadcrumbs"
import { collectionApi } from "../api"
import classNames from "classnames"
import { useCart } from "../utils/useCart"
import { whatsAppPhoneNumber } from "../config"

export default function Header() {
	const { t } = useTranslation()

	const location = useLocation()

	const logoStr = "EDUARD BELSKY"

	const [collections, setCollections] = useState(null)
	const [mobileMenuVisibility, setMobileMenuVisibility] = useState(false)

	const currentYear = new Date().getFullYear()
	
	const currentPage = useMemo(() => location.pathname, [location.pathname])
	const isHomePage = useMemo(() => currentPage === routes.home, [currentPage])

	const { cart } = useCart();

	const isActive = useCallback((route) => {
		return currentPage === route
	}, [currentPage])

	const menuItems = useMemo(() => {
		const collectionsMenu = collections?.reduce((acc, { title, slug }) => {
			acc[title] = { link: routes.collections.show(slug) }
			return acc
		}, {})

		return {
			[t("Home")]: {
				link: routes.home,
			},
			[t("News")]: {
                link: routes.articles.index,
            },
			[`year-${currentYear}`]: {
                link: routes.newWorks.index,
				label: `${currentYear}`
            },
			...collectionsMenu,
			[t("Works")]: {
				link: routes.works.index,
			},
			...collectionsMenu,
		}
	}, [collections, t])

	useEffect(() => {
		if (mobileMenuVisibility) {
		  document.body.style.overflow = 'hidden'
		} else {
		  document.body.style.overflow = 'auto'
		}
	  
		return () => {
		  document.body.style.overflow = 'auto'
		}
	}, [mobileMenuVisibility])

	const [scrolled, setScrolled] = useState(false)

	useEffect(() => {
	  const handleScroll = () => {
		if (window.scrollY > 0) {
		  setScrolled(true)
		} else {
		  setScrolled(false)
		}
	  }
  
	  window.addEventListener("scroll", handleScroll)
  
	  return () => {
		window.removeEventListener("scroll", handleScroll)
	  }
	}, [])

	useEffect(() => {
		const getCollections = async () => {
			const response = await collectionApi.list({ is_in_menu: true })
			setCollections(response)
		}
		getCollections()
	}, [])

	return (
		<>
			<header className="hidden sm:block w-full sticky top-0 bg-white z-20">
				{/* Menu (Desktop) */}
				<div className="w-full border border-b-slate-200">
					<Inner>
						<div className="py-4 items-center flex justify-center relative px-5">
							<Link to={routes.home}>
								<h1 className="text-5xl">{logoStr}</h1>
							</Link>
							<div className="flex gap-4 absolute right-0 px-5">
								<Link to={`https://wa.me/${whatsAppPhoneNumber}`}
								className="cursor-pointer"
								>
									<svg width="26" height="26" viewBox="0 0 24 24" fill="black" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.031-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.941 1.164-.173.199-.347.223-.644.075-.297-.149-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.151-.172.2-.296.298-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51-.173-.008-.372-.01-.571-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.077 4.363.709.306 1.262.489 1.694.626.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zm-5.421 7.617h-.001a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374A9.86 9.86 0 012.1 12.309c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.995c-.003 5.45-4.437 9.884-9.888 9.884zm8.413-18.297A11.815 11.815 0 0011.988 0C5.373 0 .111 5.262.109 11.873c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.868 11.868 0 005.626 1.432h.005c6.616 0 11.879-5.262 11.882-11.873a11.82 11.82 0 00-3.485-8.423z"/>
                                    </svg>
								</Link>
								<Link
									to={routes.contact.index} 
									className="cursor-pointer hover:text-slate-400 transition" 
									title={t("Contact us")}
								>
									<Mail size={28} />
								</Link>
								<Link to={routes.cart.index} className="relative cursor-pointer hover:text-slate-400 transition" title={t("Cart")}>
								<ShoppingCart size={28} />
								{cart.length > 0 && (
									<span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
										{cart.length}
									</span>
								)}
								</Link>
								<LanguageSelect />
								<CurrencySelect />
							</div>
						</div>
					</Inner>
				</div>
				<nav className="w-full border border-t-0 border-b-slate-200">
					<ul className="w-full my-3 flex justify-center gap-10">
						{Object.entries(menuItems).map(([key, { link, active = isActive(link), label }], index) => (
							<Link key={index} to={link} className={`text-lg uppercase font-light hover:text-slate-400 transition-all ${active ? 'text-slate-400' : ''}`}>
								<li key={index} className={`flex align-center gap-1 hover:text-slate-400 transition-all cursor-pointer ${active ? 'text-slate-400' : ''}`}>
									<span className="text-lg uppercase font-light">{label || key}</span>
								</li>
							</Link>
						))}
					</ul>
				</nav>
				{/* Breadcrumbs */}
				{/* {!isHomePage && <Breadcrumbs />} */}
			</header>
			{/* Menu (Mobile) */}
			<nav className={
				classNames("sm:hidden flex justify-between items-center fixed w-full top-0 p-5 z-50 transition-all ease-in-out duration-1000", {
					"bg-transparent text-white": !scrolled && isHomePage,
					"bg-white text-black": scrolled || !isHomePage,
					"fixed": isHomePage,
					"sticky": !isHomePage,
				})
			}>
				<Link to={routes.home}>
					<h1 className="text-2xl">{logoStr}</h1>
				</Link>
				<div className="flex items-center gap-4">
					<Link to={routes.cart.index} className="relative cursor-pointer hover:text-slate-400 transition" title={t("Cart")}>
						<ShoppingCart size={32} />
						{cart.length > 0 && (
							<span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
								{cart.length}
							</span>
						)}
					</Link>
					<Menu size={36} onClick={() => setMobileMenuVisibility(true)} />
				</div>
			</nav>
			<div
				className={classNames({
					'fixed top-0 left-0 w-full justify-center bg-black/90 z-[99999] overflow-hidden transition-all duration-500': true,
					'h-0 opacity-0': !mobileMenuVisibility,
					'h-full opacity-100': mobileMenuVisibility,
				})}
			>
				<X
					className={classNames({
						"absolute right-5 text-gray-600 hover:text-white transition-all duration-700": true,
						'-top-10 opacity-0': !mobileMenuVisibility,
						'top-5 opacity-100': mobileMenuVisibility,
					})}
					size={48}
					onClick={() => setMobileMenuVisibility(false)}
				/>
				<ul
					className={classNames({
						"w-full h-full mt-28 flex flex-col justify-start items-center gap-8 text-2xl text-gray-600": true,
						'hidden': !mobileMenuVisibility,
					})}
				>
					{/* Language and Currency Select */}
					<li className="flex flex-row items-center gap-12">
						<LanguageSelect/>
						<CurrencySelect />
					</li>
					{Object.entries(menuItems).map(([key, { link, active = isActive(link), label }], index) => (
						<Link
							key={index}
							to={link}
							onClick={() => setMobileMenuVisibility(false)}
						>
							<li
								className={classNames(`flex uppercase align-center gap-1 hover:text-white cursor-pointer transition-all mobile-menu-item`, {
									'text-white': active,
									'opacity-0': !mobileMenuVisibility,
									'opacity-100': mobileMenuVisibility,
								})}
								style={{ '--delay': `${index * 120 + 500}ms` }}
							>
								{label || key}
							</li>
						</Link>
					))}
				</ul>
			</div>
		</>
	)
}